import { useMemo } from "react";

import {
  ahPostureLabel,
  type EnrichedMapAccount,
} from "./portfolioMapVisualHelpers";
import { useSignalPresentation } from "./useSignalPresentation";
import { useHealthScoreThresholds } from "../../Hooks/useHealthScoreThresholds";
import type { PortfolioAccountRow } from "../../Store/Reducers/MyPortfolioOverviewSlice";

interface Props {
  /** Working-set label, e.g. "Priority Growth in BH × Fit" (mock
   * pmWorkingSetLabel). */
  title: string;
  accounts: EnrichedMapAccount[];
  /** For the AL join — PortfolioMapAccount has no `al` field (it lives on
   * the separate my-portfolio/overview endpoint), so it's joined here by
   * account id rather than extending the map's own data model. */
  overviewAccounts: PortfolioAccountRow[];
  /** Only for quadrant sets — mock togglePMBoundaryInclusion. */
  boundaryControl?: { checked: boolean; onToggle: () => void };
  onClear: () => void;
  onRemove: (accountId: string) => void;
  /** Omitted when Signal service / Open Actions is off — link is not shown. */
  onReviewActions?: () => void;
  onReviewPriorities: () => void;
  onReviewGoals: () => void;
}

function formatNet(net: number | null): string {
  if (net == null) return "—";
  const rounded = Math.round(net);
  return rounded >= 0 ? `+${rounded}` : `${rounded}`;
}

/** Right-panel working-set list (mock renderPMComparisonPanel /
 * #pm-rp-state3): a region click or manual selection lists its accounts
 * here first; the table modal only opens from "Compare N accounts". */
const PortfolioMapComparisonPanel: React.FC<Props> = ({
  title,
  accounts,
  overviewAccounts,
  boundaryControl,
  onClear,
  onRemove,
  onReviewActions,
  onReviewPriorities,
  onReviewGoals,
}) => {
  const { signalsVisible } = useSignalPresentation();
  const { accountHealth: ahThresholds } = useHealthScoreThresholds();
  const alByAccountId = useMemo(
    () => new Map(overviewAccounts.map(a => [a.customer_id, a.al_level])),
    [overviewAccounts]
  );

  return (
    <div className="flex h-full flex-col gap-3">
      <div>
        <div className="flex items-center justify-between gap-2">
          <p className="truncate text-sm font-extrabold text-gray-900">
            {title}
          </p>
          <button
            type="button"
            onClick={onClear}
            className="shrink-0 text-[11px] font-bold text-brand hover:underline"
          >
            Clear
          </button>
        </div>
        <p className="mt-0.5 text-[11px] text-gray-400">
          {accounts.length} account{accounts.length === 1 ? "" : "s"} ·
        </p>
      </div>

      {boundaryControl && (
        <label className="flex items-center gap-1.5 rounded-md bg-gray-50 px-2 py-1.5 text-[11px] font-semibold text-gray-600">
          <input
            type="checkbox"
            checked={boundaryControl.checked}
            onChange={boundaryControl.onToggle}
          />
          Include boundary accounts
        </label>
      )}

      {accounts.length === 0 && (
        <p className="text-[11px] text-gray-400">
          No accounts match this region under the current boundary rule.
        </p>
      )}

      <div className="flex max-h-[280px] flex-col gap-2 overflow-y-auto">
        {accounts.map(a => {
          const al = alByAccountId.get(a.account_id) ?? null;
          return (
            <div
              key={a.account_id}
              data-compare-account={a.account_id}
              className="relative rounded-lg border border-gray-200 bg-gray-50 p-2.5"
            >
              <button
                type="button"
                onClick={() => onRemove(a.account_id)}
                aria-label={`Remove ${a.account_name} from comparison`}
                className="absolute right-2 top-2 text-gray-400 hover:text-gray-700"
              >
                ×
              </button>
              <p className="truncate pr-4 text-[12.5px] font-bold text-gray-900">
                {a.account_name}
              </p>
              <p className="mt-0.5 text-[11px] text-gray-500">
                Fit {a.fit != null ? Math.round(a.fit) : "—"} · BH{" "}
                {a.bh != null ? Math.round(a.bh) : "—"} · AH{" "}
                {a.ah != null ? Math.round(a.ah) : "—"} ·{" "}
                {ahPostureLabel(a.ah, ahThresholds, a.ah_cluster)}
              </p>
              <p className="mt-0.5 text-[11px] text-gray-500">
                {al != null ? `AL ${al}` : <span title="Unassessed">AL ?</span>}{" "}
                · Net {formatNet(a.net_investment_signal)}
                {signalsVisible
                  ? ` · ${a.active_signal_count} active signal${
                      a.active_signal_count === 1 ? "" : "s"
                    }`
                  : ""}
              </p>
            </div>
          );
        })}
      </div>

      <div className="mt-1 flex flex-col gap-1.5 border-t border-gray-200 pt-2.5">
        {onReviewActions ? (
          <button
            type="button"
            onClick={onReviewActions}
            className="text-left text-xs font-semibold text-purple-700 hover:underline"
          >
            Review actions →
          </button>
        ) : null}
        <button
          type="button"
          onClick={onReviewPriorities}
          className="text-left text-xs font-semibold text-purple-700 hover:underline"
        >
          Review priorities →
        </button>
        <button
          type="button"
          onClick={onReviewGoals}
          className="text-left text-xs font-semibold text-purple-700 hover:underline"
        >
          View in Portfolio Grid →
        </button>
      </div>
    </div>
  );
};

export default PortfolioMapComparisonPanel;
